/**
 * useAdminSettings.ts
 *
 * Service-wide index settings — the crawl root and division allowlist — as
 * opposed to the per-user scope filters in settings/useSettings.ts.
 *
 * A 403 is a normal outcome, not an error: non-admins get one, and the settings
 * pane uses it to decide whether to render the admin section at all. So `retry`
 * is off (retrying a permission failure just delays the same answer) and the
 * caller reads `error.code === "NOT_ADMIN"` rather than treating any failure as
 * broken.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "../api/client";
import type {
  AdminSettings,
  ApiError,
  UpdateAdminSettingsRequest,
} from "../api/types";

const ADMIN_SETTINGS_KEY = ["admin", "settings"];

export function useAdminSettings(): UseQueryResult<AdminSettings, ApiError> {
  return useQuery<AdminSettings, ApiError>({
    queryKey: ADMIN_SETTINGS_KEY,
    queryFn: () => api.getAdminSettings(),
    retry: false,
  });
}

export function useUpdateAdminSettings(): UseMutationResult<
  AdminSettings,
  ApiError,
  UpdateAdminSettingsRequest
> {
  const queryClient = useQueryClient();
  return useMutation<AdminSettings, ApiError, UpdateAdminSettingsRequest>({
    mutationFn: (body) => api.updateAdminSettings(body),
    onSuccess: (updated) => {
      queryClient.setQueryData(ADMIN_SETTINGS_KEY, updated);
      // Changing the crawl root changes what the next sync indexes, so cached
      // search results and the index-status banner are both now suspect.
      void queryClient.invalidateQueries({ queryKey: ["projects", "search"] });
      void queryClient.invalidateQueries({ queryKey: ["index", "status"] });
    },
  });
}

export function useResetAdminSetting(): UseMutationResult<
  AdminSettings,
  ApiError,
  string
> {
  const queryClient = useQueryClient();
  return useMutation<AdminSettings, ApiError, string>({
    mutationFn: (key) => api.resetAdminSetting(key),
    onSuccess: (updated) => {
      queryClient.setQueryData(ADMIN_SETTINGS_KEY, updated);
      void queryClient.invalidateQueries({ queryKey: ["projects", "search"] });
    },
  });
}
